<script setup lang="ts">
//#region import
import { ref, watch, computed } from 'vue';
import { debounce } from 'lodash-es';
import type { ElDropdown } from 'element-plus';
//#endregion

//#region types
interface DropdownItems {
  param: string,
  label: string,
  searchString?: string,
}
interface Props {
  items?: DropdownItems[],
  selected?: any,
  searchPlaceholder?: string,
  notFoundMessage?: string,
  placement?: InstanceType<typeof ElDropdown>['$props']['placement'],
  menuClass?: string,
  debounceTimeout?: number,
  disabled?: boolean,
  isSearchable?: boolean,
}
//#endregion

//#region props
const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  selected: () => [],
  searchPlaceholder: '',
  notFoundMessage: '',
  placement: 'bottom-start',
  menuClass: '',
  debounceTimeout: 0,
  disabled: false,
  isSearchable: true,
});
//#endregion

//#region emits
const emit = defineEmits(['select']);
//#endregion

//#region data
const active = ref<boolean>(false);
const filter = ref<string>('');
const pFilter = ref<string>('');
//#endregion

//#region computed
const setPFilterDebounced = computed(() => {
  return debounce((value) => {
    pFilter.value = value;
  }, props.debounceTimeout);
});

const pItems = computed<DropdownItems[]>(() => {
  return props.items.map((item) => ({
    ...item,
    searchString: `${item.param.toLowerCase()}\r\n${item.label.toLowerCase()}`,
  }));
});

const itemsNotSelected = computed(() => {
  return pItems.value.filter((item) => !props.selected.includes(item.param));
});

const itemsFiltered = computed(() => {
  const filterLowerCase = pFilter.value.toLowerCase();
  return itemsNotSelected.value.filter((item) => item.searchString?.includes(filterLowerCase));
});
//#endregion

//#region watch
watch(() => filter.value, (value) => {
  setPFilterDebounced.value(value);
});
//#endregion

//#region methods
const handleParamClick = (item: DropdownItems) => {
  emit('select', item.param);
};

const handleVisibleChange = (value: boolean) => {
  active.value = value;
  if (value) {
    filter.value = '';
  }
};
//#endregion
</script>

<template>
  <div class="ui-dropdown-select">
    <ElDropdown
      v-if="!disabled"
      trigger="click"
      :placement="placement"
      :disabled="disabled"
      @visible-change="handleVisibleChange"
      @command="handleParamClick"
    >
      <slot
        name="activator"
        v-bind="{ active }"
      />
      <template v-slot:dropdown>
        <ElDropdownMenu
          class="ui-dropdown-select__dropdown-menu"
          :class="menuClass"
        >
          <div class="ui-dropdown-select__search">
            <UiInput
              v-if="isSearchable"
              v-model="filter"
              :placeholder="searchPlaceholder"
              width="100%"
              is-search
            />
          </div>
          <div
            v-if="itemsFiltered.length"
            class="ui-dropdown-select__list-container"
          >
            <ElDropdownItem
              v-for="item in itemsFiltered"
              :key="item.param"
              class="ui-dropdown-select__list-item"
              :command="item"
            >
              {{ item.label }}
            </ElDropdownItem>
          </div>
          <template v-else-if="notFoundMessage">
            <slot name="not-found">
              <div class="ui-dropdown-select__not-found">
                {{ notFoundMessage }}
              </div>
            </slot>
          </template>
        </ElDropdownMenu>
      </template>
    </ElDropdown>
    <div
      v-else
      class="ui-dropdown-select__activator-disabled"
    >
      <slot
        name="activator"
        v-bind="{ active }"
      />
    </div>
  </div>
</template>

<style lang="scss">
  .ui-dropdown-select {
    &__dropdown-menu {
      &.el-tooltip.el-popper{
        box-shadow: var(--el-box-shadow-light) !important;
      }
      z-index: 90000 !important;
      max-width: 600px;
      .el-dropdown-menu__item{
        font-size: 13px;
        color: var(--text-body);
        height: 30px !important;
        line-height: 1;
        padding: 8px 26px 8px 12px;
        &:not(.is-disabled) {
          &:focus,
          &:hover {
            background: var(--background-brand-subtle);
            color: var(--text-body);
          }
        }
      }
    }
    &__list-item{
      display: flex;
      align-items: center;
    }
    &__list-container {
      overflow: auto;
    }
    &__not-found {
      font-size: 14px;
      height: 72px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--text-body-tertiary);
    }
    &__search {
      border-bottom: 1px solid var(--el-border-color-light);
      padding: 10px;
    }
  }
</style>
